import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import {
  GetProductCodeByWebIdQuery,
  GetProductCodeByWebIdQueryVariables,
} from '@notino/shared/definitions/types';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import {
  extendSAPageViewTracking,
  trackAddToCard,
  trackAddToWishList,
  trackRemoveFromWishList,
  trackViewItem,
  trackViewItemList,
} from '../../tracking';

import { SkaEventName, SkaTrackingEvent } from './model';
import productCodeQuery from './productCode.graphql';

export const useTracking = () => {
  const { getTimeFromInit } = useTrackingContext();
  const {
    product: { brand },
  } = useProductDetailContext();
  const apollo = useApolloClient();

  const loadProductCodes = React.useCallback(
    async (ids: string[]) => {
      const { data } = await apollo.query<
        GetProductCodeByWebIdQuery,
        GetProductCodeByWebIdQueryVariables
      >({
        query: productCodeQuery,
        variables: { ids },
        fetchPolicy: 'cache-first',
      });
      return data?.vpProductByIds ?? [];
    },
    [apollo]
  );

  const dispatchTrackingEvent = React.useCallback(
    (event: SkaTrackingEvent) => {
      const eventPayloadParams = event.payload.payload;

      switch (event.payload.name) {
        case SkaEventName.OpenProductDetails:
          void trackViewItem(eventPayloadParams, loadProductCodes);
          return;
        case SkaEventName.ViewItemList:
          void trackViewItemList(eventPayloadParams.items, loadProductCodes);
          return;
        case SkaEventName.AddToWishList:
          void trackAddToWishList(
            eventPayloadParams,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case SkaEventName.RemoveFromWishList:
          void trackRemoveFromWishList(
            eventPayloadParams,
            getTimeFromInit(),
            loadProductCodes
          );
          return;
        case SkaEventName.AddToCart:
          void trackAddToCard(eventPayloadParams, loadProductCodes);
          return;
        case SkaEventName.PageView:
          extendSAPageViewTracking(eventPayloadParams, brand?.id);
          return;
        default:
          break;
      }
    },
    [getTimeFromInit, loadProductCodes, brand]
  );

  return { dispatchTrackingEvent };
};
